import React from "react";
import { useOutletContext } from "react-router-dom";
import CreateClientAccountCard from "../components/CreateClientAccountCard";
import ClientAccountsList from "../components/ClientAccountsList";
import SpecialistsAdmin from "../components/SpecialistsAdmin";
import CrIcon from "../components/CrIcon";

/*
 * Client accounts — BossDrop's OWN admin area, and the only page in the hub
 * that is not about the workspace you are signed in to.
 *
 * This is tier 1 of the two invite paths: it creates the OWNER account for a
 * brand new client company, i.e. a whole new customer workspace. Tier 2, the
 * one every client's own owner or admin can use, lives in Team Directory in
 * the Control Room and only ever invites a person into the workspace they are
 * already in. Keeping the two on separate pages is the point: nobody should
 * be one mis-click away from minting a customer when they meant to add a
 * colleague.
 *
 * Gating is defence in depth, not the real lock. The real lock is the server:
 * POST /auth/admin/invites sits behind requirePlatformAdmin and checks
 * PLATFORM_ADMIN_EMAILS itself. The route in App.js redirects a non-admin
 * away, the nav item is not rendered for them, and this page still checks
 * before it draws the card.
 */

export default function ClientAccountsPage() {
  const { currentUser } = useOutletContext();
  // Bumped after each invite so the client list below reloads with it. Above
  // the early return: hooks must run on every render.
  const [clientsKey, setClientsKey] = React.useState(0);

  // App.js already redirects a non-platform-admin away from /clients, so this
  // is the belt to that route's braces. Rendering nothing (rather than an
  // explanation) is deliberate: a stray reader learns nothing about what this
  // page would have held.
  if (!currentUser?.isPlatformAdmin) return null;

  return (
    <section className="panel controlRoom">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>Client accounts</h2>
        <span className="chip">BossDrop admin</span>
      </div>
      <p className="muted" style={{ marginTop: 6 }}>
        BossDrop's own admin area, for onboarding new customers. Only you can
        see this page. Nothing here touches the workspace you are signed in to:
        to bring a colleague into that workspace, use Team Directory in the
        Control Room.
      </p>

      <div className="crGroup">
        <CrIcon name="team" />
        New customer
      </div>

      <CreateClientAccountCard onInvited={() => setClientsKey((k) => k + 1)} />

      <div style={{ marginTop: 28 }}>
        <ClientAccountsList refreshKey={clientsKey} />
      </div>

      <SpecialistsAdmin />
    </section>
  );
}
